/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Button, Container, createStyles, CssBaseline, makeStyles, Paper, Theme, Typography } from "@material-ui/core";
import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import config from "../../config";
import { AppState } from "../../redux";
import { fetchOAuthConfigActions } from "../../redux/session/actions";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flex: 1,
    },
    loginPanel: {
      position: "relative",
      top: "20%",
      marginTop: theme.spacing(-8),
      padding: theme.spacing(4),
      justifyContent: "space-between",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    logo: {
      maxHeight: "120px",
      margin: theme.spacing(2),
      display: "inline-block",
    },
    title: {
      padding: theme.spacing(1),
    },
    explanation: {
      padding: theme.spacing(1),
    },
    form: {
      width: "100%",
    },
    submit: {
      margin: theme.spacing(3, 0),
    },
    error: {
      // background: theme.palette.error.main,
    },
    errorText: {
      color: theme.palette.error.main,
    },
  })
);

interface LoginPageProps {}

const LoginPage: FC<LoginPageProps> = props => {
  const classes = useStyles(props);

  const [authURL, setAuthURL] = useState<string>("#");

  const oauthConfig = useSelector((state: AppState) => state.session.oauth);

  const dispatch = useDispatch();
  useEffect(
    function ensureOAuthConfig() {
      if (oauthConfig === null) {
        dispatch(fetchOAuthConfigActions.request(config.dfdmHost));
      }
    },
    [oauthConfig, dispatch]
  );

  useEffect(
    function setLoginLink() {
      if (oauthConfig === null) return;

      const scopes = [
        "openid",
        "profile",
        "evaluations",
        "certifications",
        "write:evaluation",
        "read:facility",
        "read:farm",
        "read:coop",
        "read:user",
        'write:action_plan',
        "write:attachment",
        "read:definition"
      ].join(' ');

      setAuthURL(
        `${oauthConfig.authorization_endpoint}?response_type=code&client_id=${
          config.oauth.client_id
        }&redirect_uri=${config.oauth.redirect_uri}&scope=${scopes}`
      );
    },
    [oauthConfig]
  );

  return (
    <Container component="main" maxWidth="xs" className={classes.root}>
      <CssBaseline>
        <Paper className={classes.loginPanel}>
          <img
            className={classes.logo}
            src="/eval/images/logo.svg"
            alt="NDFP Logo"
          />

          <Typography variant="h6" className={classes.title}>
            Sign in
          </Typography>

          <Typography variant="body1" className={classes.explanation}>
            To continue, sign in with your existing DairyFARM account.
          </Typography>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            href={authURL}
            disabled={authURL === '#'}
            onKeyPress={event => {
              if (event.key === " ") window.location.assign(authURL);
            }}
          >
            Click to sign in
          </Button>
        </Paper>
      </CssBaseline>
    </Container>
  );
};

export default LoginPage;
